/** What a topbar pill leads to, and what it is called. */
import { computed, type ComputedRef } from "vue";
import { useBoard } from "../stores/board.js";
import { useViewport } from "../stores/viewport.js";
import { frontKey, openWindow, windowKey, windowTitle } from "./windows.js";
import { openTaskFromView, openTaskHere } from "./taskRoute.js";
import { waitingSessions } from "./autoPin.js";
import { pinKey, type Pin } from "@shared/settings.js";

export interface Pill {
  key: string;
  pin: Pin;
  label: string;
  /** What kind of place it is, beside the label — "tmux", "queued" — so a task
   *  and its session, which share a title, can be told apart. */
  kind: string | null;
  /** It is what is on screen. */
  current: boolean;
  /** Its tooltip, where the label is only part of it. */
  detail?: string;
  /** A session whose Claude is waiting for an answer (lib/autoPin.ts). */
  waiting?: boolean;
}

/** The pins the board on screen can show, as pills. A task needs to be one the
 *  store holds (and named: a draft is never pinned); a session or queued
 *  request, the tmux view (admin, on a coding board). */
export function usePills(): ComputedRef<Pill[]> {
  const board = useBoard();
  const viewport = useViewport();
  const titleOf = (id: string | null): string | undefined => (id ? board.tasks.get(id)?.title || undefined : undefined);
  return computed(() =>
    viewport.pins.flatMap((pin): Pill[] => {
      const key = pinKey(pin);
      switch (pin.kind) {
        case "settings":
          if (!board.me?.isAdmin) return [];
          return [{ key, pin, label: windowTitle(pin), kind: null, current: frontKey.value === windowKey(pin) }];
        case "task":
          if (!board.tasks.has(pin.id)) return [];
          return [{ key, pin, label: titleOf(pin.id) ?? "Untitled", kind: null, current: board.selectedId === pin.id }];
        case "session": {
          if (!board.tmuxAvailable) return [];
          const shown = viewport.view === "tmux" && viewport.tmuxShown?.session === pin.name;
          const waiting = waitingSessions.value.has(pin.name);
          const label = titleOf(pin.taskId) ?? pin.name;
          return [{ key, pin, label, kind: "tmux", current: shown, waiting, detail: waiting ? `${label} (tmux) — Claude is waiting for an answer` : undefined }];
        }
        case "queued": {
          if (!board.tmuxAvailable) return [];
          const shown = viewport.view === "tmux" && viewport.tmuxShown?.queued === pin.id;
          return [{ key, pin, label: titleOf(pin.taskId) ?? "Queued prompt", kind: "queued", current: shown }];
        }
        case "files": {
          if (!board.tmuxAvailable) return [];
          const f = viewport.filesShown;
          const shown = viewport.view === "tmux" && !!f && f.repo === pin.repo && f.worktree === pin.worktree && f.path === pin.path;
          const tree = pin.worktree ? `${pin.repo} ⎇ ${pin.worktree}` : pin.repo;
          const label = pin.path ? pin.path.slice(pin.path.lastIndexOf("/") + 1) : (pin.worktree ?? pin.repo);
          return [{ key, pin, label, kind: "files", current: shown, detail: pin.path ? `${tree}: ${pin.path}` : tree }];
        }
      }
    }),
  );
}

/** Go to what a pill is pinned to. */
export function openPin(pin: Pin): void {
  const board = useBoard();
  const viewport = useViewport();
  switch (pin.kind) {
    case "settings":
      openWindow(pin);
      return;
    case "task":
      // The drawer is beside the tree and the chart, and over the tmux view; anywhere else, back to the board.
      if (viewport.view === "board" || viewport.view === "timeline" || (viewport.view === "tmux" && board.tmuxAvailable)) openTaskHere(pin.id);
      else openTaskFromView(pin.id);
      return;
    case "session":
      viewport.tmuxPick = { session: pin.name };
      viewport.view = "tmux";
      return;
    case "queued":
      viewport.tmuxPick = { queued: pin.id };
      viewport.view = "tmux";
      return;
    case "files": {
      // Another tree replaces the editor's without asking, so ask here.
      const f = viewport.filesShown;
      if (f?.dirty && (f.repo !== pin.repo || f.worktree !== pin.worktree)
        && !confirm(`${f.path ?? "The open file"} has changes that are not saved. Throw them away?`)) return;
      viewport.tmuxPick = { files: { repo: pin.repo, worktree: pin.worktree, path: pin.path } };
      viewport.view = "tmux";
      return;
    }
  }
}
